
// Exporting both 't' for direct usage and 'translations' for the LanguageContext
export const t = {
  meta: {
    title: "82 Marketing - E-commerce Marketing Agency",
    desc: "Marketing agency specialized in increasing sales for online stores."
  },
  header: {
    services: "Services",
    webApps: "Web Apps",
    mobileApps: "Mobile Apps",
    studio: "82 Studio",
    leads: "82 Leads",
    flightdeck: "FlightDeck",
    cataboost: "Cataboost",
    mazarion: "Mazarion",
    about: "About Us",
    caseStudies: "Case Studies",
    testimonials: "Testimonials",
    faq: "FAQ",
    contact: "Contact",
    cta: "Get Offer"
  },
  apps: {
    webAppsTitle: "Web Apps",
    mobileAppsTitle: "Mobile Apps",
    studio: {
      name: "82 Marketing Studio",
      subtitle: "AI Video & Image Ads",
      badge: "AI",
      href: "https://studio.82marketing.com"
    },
    leads: {
      name: "82 Leads",
      subtitle: "B2B Lead Generation & Intelligence",
      badge: "Leads",
      href: "https://82leads.com/"
    },
    flightdeck: {
      name: "FlightDeck",
      subtitle: "Marketing Ops & Cockpit",
      badge: "App",
      href: "https://flightdeck.boo"
    },
    cataboost: {
      name: "CataBoost.com",
      subtitle: "Product Feed Management",
      badge: "Feed",
      href: "https://cataboost.com"
    },
    mazarion: {
      name: "Mazarion",
      subtitle: "Holistic Self-Discovery & Archetypes",
      badge: "Mobile",
      href: "https://mazarion.com"
    }
  },
  hero: {
    titleStart: "We scale your",
    titleHighlight: "e-commerce sales",
    subtitle: "We manage Google, Meta, Bing, and TikTok campaigns that transform traffic into real customers, quickly and measurably.",
    ctaPrimary: "I want more sales",
    ctaSecondary: "See results",
    experts: "Experts on major advertising platforms"
  },
  studio: {
    label: "Proprietary Technology",
    title: "82 Marketing Studio",
    description: "Harness the power of Google's advanced AI models—Google Veo for video and Gemini Imagen for images—to create stunning, professional advertisements in minutes without requiring professional editing skills.",
    feature1: {
      title: "Video Ads (Google Veo)",
      desc: "Generate dynamic video content from text or images with precise camera motion and sequence control."
    },
    feature2: {
      title: "Image Ads (Gemini Imagen)",
      desc: "Create high-resolution marketing images in multiple aspect ratios simultaneously for all social platforms."
    },
    cta: "Try the Studio"
  },
  cataboost: {
    label: "Product Feed Optimization",
    title: "CataBoost.com",
    description: "The ultimate solution for e-commerce product feed management. Optimize your catalog for Google Shopping, Meta Ads, and marketplaces to maximize reach and ROAS.",
    feature1: {
      title: "Smart Filtering & Mapping",
      desc: "Create custom rules to exclude low-margin products and enhance titles/descriptions for better search visibility."
    },
    feature2: {
      title: "Real-time Multichannel Sync",
      desc: "Keep your stock, prices, and product data perfectly synchronized across all advertising platforms 24/7."
    },
    cta: "Boost Your Catalog"
  },
  services: {
    label: "Specialized Services",
    title: "Performance Marketing Solutions for E-commerce",
    description: "We focus exclusively on strategies that bring measurable results: more customers, higher sales, and excellent ROI.",
    items: [
      {
        title: 'Google Ads & Shopping',
        description: 'We maximize visibility and sales through Search, Shopping, Display, and YouTube campaigns, precisely targeting the right customers at the moment of decision.',
        features: [
          'Advanced bidding strategies (tROAS, tCPA)',
          'Product feed optimization for Merchant Center',
          'Performance Max campaigns for full coverage',
          'Dynamic remarketing for abandoned cart recovery',
        ],
      },
      {
        title: 'Meta Ads (Facebook & Instagram)',
        description: 'We build creative campaigns that convert, from awareness to sale, leveraging Meta audiences to scale your business.',
        features: [
          'Advanced audience segmentation (Lookalike, Custom)',
          'A/B testing of creatives and ad copy',
          'Conversion optimization with API & Pixel',
          'Advantage+ campaigns for automated scaling',
        ],
      },
      {
        title: 'Microsoft (Bing) Ads',
        description: 'We access a valuable and often untapped audience on the Microsoft network, often with a lower CPA than Google.',
        features: [
          'Easy import of campaigns from Google Ads',
          'Precise demographic targeting',
          'Ad extensions for increased visibility',
          'Access to LinkedIn audience',
        ],
      },
      {
        title: 'TikTok Ads',
        description: 'We capture the attention of younger generations with authentic and creative video content, turning trends into sales for your brand.',
        features: [
          'Creative concepts adapted to the platform',
          'Spark Ads campaigns for authenticity',
          'Interest and behavior targeting',
          'Optimization for conversions and lead generation',
        ],
      },
      {
        title: 'Online Store Creation',
        description: 'We build high-performance online stores from scratch, optimized for conversion and ready for scaling, on the most popular e-commerce platforms.',
        features: [
          'Development on Shopify, WooCommerce, Magento 2',
          'Modern UX/UI design personalized for your brand',
          'Mobile speed and performance optimization',
          'Full integration with payments and couriers',
        ],
      },
      {
        title: 'Email Marketing & Automation',
        description: 'We create automated email sequences (Klaviyo) that recover abandoned carts, retain customers, and generate recurring sales.',
        features: [
          'Platform setup and integration (e.g., Klaviyo)',
          'Automations for abandoned carts and win-back',
          'Advanced subscriber list segmentation',
          'Periodic newsletter campaigns and design',
        ],
      },
    ]
  },
  process: {
    label: "How We Work",
    title: "Our 4-Step Process",
    description: "A proven system that turns visitors into loyal customers, without guesswork or budget waste.",
    steps: [
      {
        title: "Audit & Analysis",
        description: "We analyze your account history, identify budget leaks, and find untapped growth opportunities."
      },
      {
        title: "Strategy & Setup",
        description: "We create a custom campaign structure and configure advanced tracking (GTM, Pixel) for precise data."
      },
      {
        title: "Launch & Optimization",
        description: "We launch campaigns and monitor performance daily, adjusting bids and creatives to maximize ROAS."
      },
      {
        title: "Reporting & Scaling",
        description: "We analyze results monthly, identify winning products, and gradually increase budgets to scale profit."
      }
    ]
  },
  about: {
    label: "About Us",
    title: "Partners in Your Digital Growth",
    p1: "At 82 Marketing, we are not just another agency. We are an extension of your team, a strategic partner formed by specialists passionate about performance marketing, dedicated exclusively to the success of online stores. Our mission is to transform advertising budgets into real profit, using data-driven strategies, advanced technology, and total transparency.",
    p2: "We believe in close and personalized collaboration. We deeply understand your business to adapt every campaign to your unique growth objectives.",
    stats: {
      clients: "Happy Clients",
      sales: "Sales Generated",
      roi: "Average ROI",
      retention: "Retention Rate"
    }
  },
  caseStudies: {
    title: "Performance Proven by Numbers",
    description: "We don't hide behind words. Here are real snapshots from our clients' accounts and the impact of our strategies.",
    items: [
      {
        category: 'CAR WHEELS (USA)',
        title: 'Dominating the Auto Wheels Market (High-Ticket) in the USA',
        description: 'Through an aggressive Google Ads strategy and granular product feed optimization, we scaled sales to nearly $5 million in 12 months, maintaining exceptional profitability in a highly competitive niche.',
        stats: [
          { label: 'Sales Value', value: '$4.91M', highlight: true },
          { label: 'Conversions', value: '2.94K' },
          { label: 'ROAS', value: '1,233%' }
        ]
      },
      {
        category: 'MEN\'S FASHION (USA)',
        title: 'Sustainable Scaling for Men\'s Fashion Brand',
        description: 'Focus on maximizing net profit. We restructured campaigns to eliminate budget waste and scaled sales volumes, achieving a perfect balance between new orders and cost per acquisition (CPA).',
        stats: [
          { label: 'Cost', value: '$22.9K' },
          { label: 'Sales Value', value: '$79.5K', highlight: true },
          { label: 'ROAS', value: '348%' }
        ]
      },
      {
        category: 'HOME & DECO (RO)',
        title: 'Maximum Efficiency in Home & Deco on the Romanian Market',
        description: 'Short-term show of force: Rapid optimization of Performance Max campaigns generated a ROAS of nearly 10x in just two weeks, turning cold traffic into paying customers instantly.',
        stats: [
          { label: 'Cost', value: '1.97K RON' },
          { label: 'Sales Value', value: '18.8K RON', highlight: true },
          { label: 'ROAS', value: '955%' }
        ]
      }
    ]
  },
  testimonials: {
    title: "What Our Clients Say",
    description: "Our partners' opinions are our best business card.",
    items: [
      {
        quote: "Working with 82 Marketing was a game-changer. Our ROAS increased by 300% in just 6 months. A team of real professionals, result-oriented.",
        name: "Rachel",
        company: "CEO, Online Fashion Store (US)",
      },
      {
        quote: "We were impressed by the transparency and constant communication. They exceeded all our expectations and helped us reach our annual sales goals early.",
        name: "Robert",
        company: "Owner, Auto Wheels Store (US)",
      },
      {
        quote: "The 82 Marketing team truly understands the e-commerce market. Their personalized strategies brought a constant flow of new customers and increased average order value.",
        name: "Bianca",
        company: "Marketing Manager, Home & Deco Store (RO)",
      },
    ]
  },
  faq: {
    label: "FAQ",
    title: "Have questions?",
    description: "We've gathered the most frequent questions we receive from online store owners. If you don't find the answer here, contact us!",
    cta: "Talk to an expert",
    items: [
      {
        question: "Is there a minimum contract period?",
        answer: "We don't believe in tying clients down with long contracts if results don't show. We work on a monthly contract basis, with the possibility to cancel anytime with 30 days' notice. We want you to stay with us because of performance, not because of contract clauses."
      },
      {
        question: "What is the recommended minimum budget to start?",
        answer: "To see relevant results and allow algorithms to learn quickly, we recommend a media budget (paid to Google/Meta) of at least $1,000 - $1,500/month. Below this threshold, optimization becomes slow, and scaling is difficult."
      },
      {
        question: "How long until the first results appear?",
        answer: "Although PPC campaigns can generate traffic instantly, optimizing for conversions and profitability (ROAS) takes time. Usually, solid results appear in the first 30 days, and campaign maturity happens in months 2-3."
      },
      {
        question: "Do I receive reports on campaign performance?",
        answer: "Absolutely. We offer real-time access to a customized dashboard, and monthly we organize a video call to analyze detailed results, profit generated, and establish the strategy for the next month."
      },
      {
        question: "Do you work with startup stores?",
        answer: "Yes, we work with startups, but it is essential that the store is fully functional, has market-validated products, and an allocated marketing budget. We can also offer consulting on site conversion rate optimization (CRO)."
      }
    ]
  },
  contact: {
    title: "Let's Talk!",
    description: "Fill out the form and we'll get back to you with a free analysis of your business.",
    formTitle: "Contact Form",
    labels: {
      name: "Full Name",
      email: "Email Address",
      phone: "Phone",
      website: "E-commerce Website",
      message: "Your Message"
    },
    placeholders: {
      name: "Your Name",
      email: "hello@82marketing.com",
      phone: "+1 (717) 450-7164",
      website: "www.your-store.com",
      message: "Tell us more about your business and goals..."
    },
    submit: "Send Request",
    info: {
      address: "Headquarters Address",
      addressCompany: "82 Marketing LLC",
      addressLocation: "Brooklyn, NY · United States",
      addressRegistered: "Registered office: 8 The Green, STE A, Dover, DE 19901",
      scheduleCall: "Schedule a Discovery Call",
      scheduleLink: "Choose a convenient date and time"
    }
  },
  footer: {
    description: "Your trusted partner for e-commerce performance. We transform traffic into sales through validated strategies.",
    usefulLinks: "Useful Links",
    contactDetails: "Contact Details",
    rights: "All rights reserved."
  },
  thankYou: {
    title: "Thank You!",
    message: "Your message has been sent successfully. We will get back to you with an answer as soon as possible.",
    button: "Back to Home"
  },
  cookie: {
    text: "We use cookies to personalize content and ads, to provide social media features and to analyze our traffic. Continuing navigation implies acceptance of our privacy policy.",
    title: "Cookie Policy",
    decline: "Decline",
    accept: "Accept All"
  }
};

// Fix: Adding translations export to satisfy contexts/LanguageContext.tsx requirements
export const translations = {
  ro: t,
  en: t
};
